import {Injectable, Injector} from "@angular/core";

import {ReportError} from "app/app.service";
import {BaseListService, BatchResponse} from "app/services/domain/base.service";
import {WorkloadDefinitionExtended} from "app/services/domain/workload-definitions.service";
import * as Models from "app/services/proxy/model/models";
import {QueryOperatorLogicNode} from "app/shared/query-group/query-operator-query-node";

import {Lookup} from "framework/services/utils.service";

@Injectable()
export class WorkloadsListService extends BaseListService<Models.WorkloadDefinitionListRequest, Models.BatchRequest, Models.WorkloadDefinition>
{
    private readonly m_workloadsByTenant: Lookup<Models.WorkloadDefinition[]> = {};

    constructor(inj: Injector)
    {
        super(inj, Models.WorkloadDefinition);
    }

    @ReportError
    public list(listReq: Models.WorkloadDefinitionListRequest): Promise<Models.ListResponse>
    {
        return this.domain.apis.workloadDefinitionsApi.list(listReq);
    }

    @ReportError
    protected async getBatchResponseImpl(req: Models.BatchRequest): Promise<BatchResponse<Models.WorkloadDefinition>>
    {
        const recordIdBatchRequest = Models.RecordIdentityBatchRequest.newInstance({ids: req?.ids.map((id) => WorkloadDefinitionExtended.newIdentity(id))});
        const workloads            = await this.domain.apis.workloadDefinitionsApi.batch(recordIdBatchRequest);

        const lookup: Lookup<Models.WorkloadDefinition> = {};
        for (const workload of workloads || []) lookup[workload.sysId] = workload;

        return {results: lookup};
    }

    protected createBatchRequest(listRequest: Models.WorkloadDefinitionListRequest,
                                 ids: string[]): Models.BatchRequest
    {
        return Models.BatchRequest.newInstance({ids: ids});
    }

    //--//

    public listAllForTenant(tenantId: string): Promise<string[]>
    {
        const workloadFilters = Models.WorkloadFilter.newInstance({tenant_id: tenantId});
        return super.listAll(Models.WorkloadDefinitionListRequest.newInstance({filters: workloadFilters}));
    }

    public async getAllForTenant(tenantId: string): Promise<Models.WorkloadDefinition[]>
    {
        if (!this.m_workloadsByTenant[tenantId])
        {
            const ids   = await this.listAllForTenant(tenantId);
            const batch = await this.batch(Models.BatchRequest.newInstance({ids: ids}));

            if (batch) this.m_workloadsByTenant[tenantId] = batch;
        }

        return this.m_workloadsByTenant[tenantId];
    }

    public async getAllWithFilters(tenantId: string,
                                   clouds: string[],
                                   cloudAccountIds: string[]): Promise<Models.WorkloadDefinition[]>
    {
        const workloadFilters = Models.WorkloadFilter.newInstance({tenant_id: tenantId});

        if (clouds?.length)
        {
            const cloudNode       = Models.WorkloadQueryCloud.newInstance({clouds: clouds});
            workloadFilters.query = QueryOperatorLogicNode.andAppendQuery(workloadFilters.query, cloudNode);
        }

        if (cloudAccountIds?.length)
        {
            const accountNode     = Models.WorkloadQueryCloudAccount.newInstance({cloud_account_ids: cloudAccountIds});
            workloadFilters.query = QueryOperatorLogicNode.andAppendQuery(workloadFilters.query, accountNode);
        }

        const ids = await this.listAll(Models.WorkloadDefinitionListRequest.newInstance({filters: workloadFilters}));
        return this.batch(Models.BatchRequest.newInstance({ids: ids}));
    }

    public resetTenantCache(tenantId: string)
    {
        this.m_workloadsByTenant[tenantId] = null;
    }

    public async getAllWorkloadNamesForTenant(tenantId: string): Promise<string[]>
    {
        const defs = await this.getAllForTenant(tenantId);
        return defs.map((def) => def.name);
    }

    public async getAllWorkloadTypesForTenant(tenantId: string): Promise<string[]>
    {
        const defs  = await this.getAllForTenant(tenantId);
        const types = defs.map((def) => def.workload_type)
                          .filter((type) => !!type);
        return Array.from(new Set(types));
    }
}
